import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowRight,
  MapPin,
  FileText,
  Scale,
  Building2,
  Factory,
  Users,
  CheckCircle2,
  ArrowDown,
} from "lucide-react";

export default function Landing() {
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>SOSTRE BALEAR | Estudio de Viabilidad — Vivienda Asequible para Baleares</title>
        <meta name="description" content="Estudio de viabilidad para el desarrollo de vivienda industrializada sobre suelo público en alquiler a empresas del sector turístico de las Illes Balears. Modelo innovador de colaboración público-privada." />
        <link rel="canonical" href="https://www.sostrebalear.com/" />
        <link rel="alternate" hreflang="es" href="https://www.sostrebalear.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="SOSTRE BALEAR | Vivienda Asequible para Baleares" />
        <meta property="og:description" content="Estudio de viabilidad para vivienda industrializada sobre suelo público en las Illes Balears." />
        <meta property="og:url" content="https://www.sostrebalear.com/" />
        <meta property="og:image" content="https://www.sostrebalear.com/assets/logos/sostre-balear.png" />
        <meta property="og:locale" content="es_ES" />
        <meta property="og:site_name" content="SOSTRE BALEAR" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="SOSTRE BALEAR | Vivienda Asequible para Baleares" />
        <meta name="twitter:description" content="Estudio de viabilidad para vivienda industrializada sobre suelo público en las Illes Balears." />
        <meta name="twitter:image" content="https://www.sostrebalear.com/assets/logos/sostre-balear.png" />
      </Helmet>
      {/* Hero Section */}
      <section
        className="relative min-h-screen flex items-center pt-20 overflow-hidden"
        data-testid="hero-section"
      >
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-emerald-50/30" />
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-10">
          <svg viewBox="0 0 400 400" className="w-full h-full">
            <pattern
              id="grid"
              width="40"
              height="40"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 40 0 L 0 0 0 40"
                fill="none"
                stroke="#22c55e"
                strokeWidth="1"
              />
            </pattern>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            {/* Text Content */}
            <div className="lg:col-span-7 space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#22c55e]/10 rounded-full text-[#22c55e] text-sm font-medium animate-fadeInUp">
                <CheckCircle2 size={16} />
                Estudio de Viabilidad Completado
              </div>

              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-tight animate-fadeInUp stagger-1"
                style={{ opacity: 0 }}
              >
                Vivienda asequible para{" "}
                <span className="text-[#22c55e]">Baleares</span>
              </h1>

              <p
                className="text-lg sm:text-xl text-slate-600 leading-relaxed max-w-2xl animate-fadeInUp stagger-2"
                style={{ opacity: 0 }}
              >
                Un modelo innovador de colaboración público-privada para
                desarrollar vivienda industrializada en suelo público,
                destinada a trabajadores del sector turístico.
              </p>

              <div
                className="flex flex-wrap gap-4 animate-fadeInUp stagger-3"
                style={{ opacity: 0 }}
              >
                <Link
                  to="/entregables"
                  className="inline-flex items-center gap-2 bg-[#22c55e] hover:bg-[#16a34a] text-white px-8 py-4 rounded-full font-medium transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                  data-testid="cta-entregables"
                >
                  Ver Entregables
                  <ArrowRight size={20} />
                </Link>
                <Link
                  to="/mapa"
                  className="inline-flex items-center gap-2 bg-white border border-slate-200 text-slate-900 hover:bg-slate-50 px-8 py-4 rounded-full font-medium transition-all"
                  data-testid="cta-mapa"
                >
                  <MapPin size={20} />
                  Ver Mapa de Suelo
                </Link>
              </div>

              <div
                className="flex items-center gap-6 pt-4 animate-fadeInUp stagger-4"
                style={{ opacity: 0 }}
              >
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <FileText size={18} className="text-[#22c55e]" />
                  <span>8 Work Packages</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Scale size={18} className="text-[#22c55e]" />
                  <span>Marco legal completo</span>
                </div>
              </div>
            </div>

            {/* Visual Element */}
            <div
              className="lg:col-span-5 relative animate-fadeInUp stagger-3"
              style={{ opacity: 0 }}
            >
              <div className="relative">
                <div className="absolute -inset-4 bg-[#22c55e]/20 rounded-3xl blur-3xl" />
                <div className="relative bg-white rounded-3xl shadow-2xl p-8 border border-slate-100">
                  <img
                    src="/assets/logos/sostre-balear.png"
                    alt="SOSTRE BALEAR - Sostenint Turisme i Residencial"
                    className="w-full h-auto"
                  />
                  <p className="text-center text-slate-600 mt-6 text-sm">
                    Sostenint Turisme i Residencial
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Scroll indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
            <ArrowDown className="text-slate-400" size={24} />
          </div>
        </div>
      </section>

      {/* Key Blocks: Reto / Solución / Mecanismo / Resultado */}
      <section className="py-20 md:py-32 bg-white" data-testid="key-blocks-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-sm font-medium tracking-wide uppercase text-[#22c55e]">
              Visión General
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3">
              El Modelo SOSTRE BALEAR
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Reto */}
            <div className="group bg-slate-50 hover:bg-white rounded-2xl p-8 border border-slate-100 hover:border-red-200 hover:shadow-lg transition-all duration-300">
              <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <span className="text-2xl font-bold text-red-600">!</span>
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                El Reto
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Crisis habitacional severa: precios +92% en una década,
                esfuerzo salarial &gt;50%, déficit de 14.000+ viviendas.
              </p>
            </div>

            {/* Solución */}
            <div className="group bg-slate-50 hover:bg-white rounded-2xl p-8 border border-slate-100 hover:border-[#22c55e]/30 hover:shadow-lg transition-all duration-300">
              <div className="w-14 h-14 bg-[#22c55e]/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Building2 className="text-[#22c55e]" size={28} />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                La Solución
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Vivienda industrializada a gran escala sobre suelo público,
                en alquiler a empresas turísticas para sus trabajadores.
              </p>
            </div>

            {/* Mecanismo */}
            <div className="group bg-slate-50 hover:bg-white rounded-2xl p-8 border border-slate-100 hover:border-blue-200 hover:shadow-lg transition-all duration-300">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Factory className="text-blue-600" size={28} />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                El Mecanismo
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Concesión administrativa + construcción industrializada
                (CLT) + fábrica local. Plazos reducidos un 20-40%.
              </p>
            </div>

            {/* Resultado */}
            <div className="group bg-slate-50 hover:bg-white rounded-2xl p-8 border border-slate-100 hover:border-amber-200 hover:shadow-lg transition-all duration-300">
              <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Users className="text-amber-600" size={28} />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                El Resultado
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Vivienda asequible (máx. 30% salario), retorno social 4-6€
                por cada euro público invertido, modelo replicable.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Modelo Triangular */}
      <section
        className="py-20 md:py-32 bg-slate-50"
        data-testid="triangular-model-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-sm font-medium tracking-wide uppercase text-[#22c55e]">
              Colaboración Público-Privada
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3">
              Modelo Triangular
            </h2>
            <p className="text-slate-600 mt-4 max-w-2xl mx-auto">
              Un ecosistema de colaboración que alinea los intereses de todos
              los actores clave.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Administración */}
            <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mb-6">
                <Scale className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">
                Administración Pública
              </h3>
              <p className="text-sm text-slate-500 mb-4">
                Govern, Consells, Ayuntamientos
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-sm text-slate-600">
                  <CheckCircle2
                    size={18}
                    className="text-[#22c55e] shrink-0 mt-0.5"
                  />
                  Cesión de suelo público sin enajenación
                </li>
                <li className="flex items-start gap-3 text-sm text-slate-600">
                  <CheckCircle2
                    size={18}
                    className="text-[#22c55e] shrink-0 mt-0.5"
                  />
                  Adaptación normativa
                </li>
                <li className="flex items-start gap-3 text-sm text-slate-600">
                  <CheckCircle2
                    size={18}
                    className="text-[#22c55e] shrink-0 mt-0.5"
                  />
                  Coordinación institucional
                </li>
              </ul>
            </div>

            {/* Concesionario */}
            <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-16 h-16 bg-[#22c55e] rounded-2xl flex items-center justify-center mb-6">
                <Factory className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">
                Concesionario Privado
              </h3>
              <p className="text-sm text-slate-500 mb-4">
                SPV (Devco/Opco)
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-sm text-slate-600">
                  <CheckCircle2
                    size={18}
                    className="text-[#22c55e] shrink-0 mt-0.5"
                  />
                  Financiación y construcción
                </li>
                <li className="flex items-start gap-3 text-sm text-slate-600">
                  <CheckCircle2
                    size={18}
                    className="text-[#22c55e] shrink-0 mt-0.5"
                  />
                  Gestión del parque de viviendas
                </li>
                <li className="flex items-start gap-3 text-sm text-slate-600">
                  <CheckCircle2
                    size={18}
                    className="text-[#22c55e] shrink-0 mt-0.5"
                  />
                  ROE atractivo (8-12%)
                </li>
              </ul>
            </div>

            {/* Empresas Turísticas */}
            <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-16 h-16 bg-amber-500 rounded-2xl flex items-center justify-center mb-6">
                <Building2 className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">
                Empresas Turísticas
              </h3>
              <p className="text-sm text-slate-500 mb-4">
                Hoteles, cadenas, servicios
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-sm text-slate-600">
                  <CheckCircle2
                    size={18}
                    className="text-[#22c55e] shrink-0 mt-0.5"
                  />
                  Alquiler garantizado
                </li>
                <li className="flex items-start gap-3 text-sm text-slate-600">
                  <CheckCircle2
                    size={18}
                    className="text-[#22c55e] shrink-0 mt-0.5"
                  />
                  Cesión a trabajadores (máx. 30% salario)
                </li>
                <li className="flex items-start gap-3 text-sm text-slate-600">
                  <CheckCircle2
                    size={18}
                    className="text-[#22c55e] shrink-0 mt-0.5"
                  />
                  Retención de talento
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Palancas Clave */}
      <section className="py-20 md:py-32 bg-white" data-testid="palancas-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-sm font-medium tracking-wide uppercase text-[#22c55e]">
              Factores de Éxito
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3">
              Tres Palancas Clave
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-[#22c55e]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <MapPin className="text-[#22c55e]" size={36} />
              </div>
              <div className="text-4xl font-bold text-[#22c55e] mb-2">
                593.000 m²
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                Suelo Público Disponible
              </h3>
              <p className="text-slate-600 text-sm">
                Inventario de parcelas públicas aptas para el desarrollo del
                programa en las Illes Balears.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Factory className="text-blue-600" size={36} />
              </div>
              <div className="text-4xl font-bold text-blue-600 mb-2">
                20-40%
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                Reducción de Plazos
              </h3>
              <p className="text-slate-600 text-sm">
                La construcción industrializada reduce drásticamente los
                tiempos de ejecución y mano de obra in situ.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Building2 className="text-amber-600" size={36} />
              </div>
              <div className="text-4xl font-bold text-amber-600 mb-2">
                80%
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                Empresas con Demanda
              </h3>
              <p className="text-slate-600 text-sm">
                Un 80% de las empresas del sector turístico necesita ya
                proporcionar alojamiento a sus empleados y precisa de una
                solución escalable.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Hoja de Ruta */}
      <section
        className="py-20 md:py-32 bg-slate-900 text-white"
        data-testid="roadmap-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-sm font-medium tracking-wide uppercase text-[#22c55e]">
              Implementación
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-3">
              Hoja de Ruta
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Corto Plazo */}
            <div className="bg-slate-800/50 rounded-2xl p-8 border border-slate-700">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-[#22c55e] rounded-xl flex items-center justify-center text-white font-bold">
                  1
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white">
                    Corto Plazo
                  </h3>
                  <p className="text-slate-400 text-sm">2025 - 2026</p>
                </div>
              </div>
              <ul className="space-y-4">
                {[
                  "Consulta contingencia IVA a la DGT",
                  "Actualización inventario de suelo público",
                  "Negociación compromisos de demanda con empresas turísticas",
                  "Diseño pliegos concesión piloto",
                  "Acuerdo financiación BEI/ICO",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2
                      size={18}
                      className="text-[#22c55e] shrink-0 mt-0.5"
                    />
                    <span className="text-slate-300 text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Medio Plazo */}
            <div className="bg-slate-800/50 rounded-2xl p-8 border border-slate-700">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center text-white font-bold">
                  2
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white">
                    Medio Plazo
                  </h3>
                  <p className="text-slate-400 text-sm">2027 - 2030</p>
                </div>
              </div>
              <ul className="space-y-4">
                {[
                  "Lanzamiento fábrica local de vivienda industrializada",
                  "Extensión del programa a otras islas",
                  "Replicación del modelo en otras regiones turísticas",
                  "Desarrollo programas de formación profesional específicos",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2
                      size={18}
                      className="text-blue-500 shrink-0 mt-0.5"
                    />
                    <span className="text-slate-300 text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Agradecimientos */}
      <section
        className="py-20 md:py-32 bg-white"
        data-testid="acknowledgments-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-sm font-medium tracking-wide uppercase text-[#22c55e]">
              Colaboradores
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3">
              Agradecimientos
            </h2>
            <p className="text-slate-600 mt-4 max-w-2xl mx-auto">
              Este estudio ha sido posible gracias a la colaboración de las
              siguientes entidades.
            </p>
          </div>

          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
            <a
              href="https://www.caib.es/webgoib/w/conselleria-turisme-cultura-i-esports"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-100 transition-all"
            >
              <img
                src="/assets/logos/aetib.png"
                alt="AETIB - Agència d'Estratègia Turística de les Illes Balears"
                className="h-16 md:h-20 w-auto grayscale hover:grayscale-0 opacity-70 hover:opacity-100 transition-all"
              />
            </a>
            <a
              href="https://clusterteib.es/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-100 transition-all"
            >
              <img
                src="/assets/logos/teib.jpg"
                alt="TEIB - Clúster de Transició Ecològica de les Illes Balears"
                className="h-16 md:h-20 w-auto grayscale hover:grayscale-0 opacity-70 hover:opacity-100 transition-all"
              />
            </a>
            <a
              href="https://www.pastoria.es"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-100 transition-all"
            >
              <img
                src="/assets/logos/pastoria.png"
                alt="PASTORIA"
                className="h-12 md:h-16 w-auto grayscale hover:grayscale-0 opacity-70 hover:opacity-100 transition-all"
              />
            </a>
            <a
              href="https://www.linkedin.com/company/m2f-advisors/about/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-100 transition-all"
            >
              <img
                src="/assets/logos/m2f.jpg"
                alt="M2F Advisors"
                className="h-16 md:h-20 w-auto grayscale hover:grayscale-0 opacity-70 hover:opacity-100 transition-all"
              />
            </a>
          </div>

          <div className="mt-16 text-center">
            <p className="text-slate-600 text-sm" data-testid="landing-pastoria">
              Estudio redactado por{" "}
              <span className="font-semibold text-[#22c55e]">
                PASTORIA PROJECT SL
              </span>
            </p>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-[#22c55e]" data-testid="final-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Explora los resultados del estudio
          </h2>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
            Accede a los 8 entregables del estudio, consulta la normativa
            aplicable y explora el mapa de suelo disponible.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/entregables"
              className="inline-flex items-center gap-2 bg-white text-[#22c55e] hover:bg-slate-50 px-8 py-4 rounded-full font-medium transition-all shadow-lg"
              data-testid="final-cta-entregables"
            >
              Ver Entregables
              <ArrowRight size={20} />
            </Link>
            <Link
              to="/documentacion"
              className="inline-flex items-center gap-2 bg-transparent border-2 border-white text-white hover:bg-white/10 px-8 py-4 rounded-full font-medium transition-all"
              data-testid="final-cta-docs"
            >
              <FileText size={20} />
              Descargar Documentos
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
